<script setup lang="ts">
import { tpgIconSearch } from '@tpg/grammar-web-icons';
import Fuse from 'fuse.js/basic';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarCheckbox,
  GrammarFooter,
  GrammarLocalLoader,
  GrammarModal,
  GrammarPrimaryButton,
  GrammarSecondaryButton,
  GrammarText,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';
import { textRed } from '@nora-monorepo/ui-shared';

import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useForwardMessageStore } from '#nora-chat-ui/stores/forward-message-store';

const { t } = useI18n();
const conversationStore = useConversationStore();
const forwardMessageStore = useForwardMessageStore();

const { sortedConversation, getConversationTitle }
  = storeToRefs(conversationStore);
const { isSending, showForwardMessageModal } = storeToRefs(forwardMessageStore);

const selectedItems = ref<string[]>([]);
const searchQuery = ref('');
const forwardFailed = ref(false);

const searchableConversations = computed(() => sortedConversation.value.map(conversationState => ({
  conversationState,
  title: getConversationTitle.value(conversationState.conversationListResult.id) ?? '',
})));

const fuse = computed(() => new Fuse(searchableConversations.value, {
  keys: ['title'],
  threshold: 0.4,
}));

const filteredConversations = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) {
    return sortedConversation.value;
  }

  return fuse.value.search(query).map(result => result.item.conversationState);
});

function toggleConversationSelection(conversationId: string) {
  if (selectedItems.value.includes(conversationId)) {
    selectedItems.value = selectedItems.value.filter(
      id => id !== conversationId,
    );
  }
  else {
    selectedItems.value.push(conversationId);
  }
}

function close() {
  forwardMessageStore.showForwardMessageModal = false;
  selectedItems.value = [];
  searchQuery.value = '';
  forwardFailed.value = false;
}

async function confirm() {
  if (isSending.value || selectedItems.value.length === 0) {
    return;
  }

  forwardFailed.value = false;
  try {
    await forwardMessageStore.forwardMessages(selectedItems.value);
  }
  catch {
    forwardFailed.value = true;
    return;
  }

  close();
}
</script>

<template>
  <GrammarModal
    :is-open="showForwardMessageModal"
    :label="t('forwardingMessage.forwardingMessagePopupTitle')"
    :show-close-button="false"
    @close="close"
  >
    <template #content>
      <!-- Only render the conversation list while open, it re-renders on every new message otherwise -->
      <div v-if="showForwardMessageModal && !isSending" class="w-[min(80vw,24rem)]">
        <p v-if="forwardFailed" role="alert" class="mb-2 text-sm" :class="textRed">
          {{ t('errors.forwardMessageError') }}
        </p>
        <GrammarTextField
          v-model:value="searchQuery"
          :clear-button="true"
          :leading-icon-template="tpgIconSearch"
          :placeholder="t('common.search')"
          :form-associated="false"
          @change="searchQuery = $event"
        />
        <div class="h-[min(60vh,28rem)] overflow-y-auto overflow-x-hidden overscroll-contain px-1">
          <div
            v-for="conversationState in filteredConversations"
            :key="conversationState.conversationListResult.id"
            class="flex items-center py-1"
          >
            <GrammarCheckbox
              :checked="selectedItems.includes(conversationState.conversationListResult.id)"
              :label="getConversationTitle(conversationState.conversationListResult.id)"
              @change="toggleConversationSelection(conversationState.conversationListResult.id)"
            />
          </div>
          <div v-if="searchQuery.trim() && !filteredConversations.length" class="py-4 text-center text-sm">
            <GrammarText :text="t('noResults', [searchQuery.trim()])" />
          </div>
        </div>
      </div>
      <div v-else-if="isSending" class="flex">
        <div class="m-auto">
          <GrammarLocalLoader />
        </div>
      </div>
    </template>

    <template #footer>
      <GrammarFooter>
        <template #left-button>
          <GrammarSecondaryButton :label="t('common.cancel')" @click="close" />
        </template>
        <template #right-button>
          <GrammarPrimaryButton
            :label="t('common.ok')"
            :disabled="isSending || selectedItems.length === 0"
            @click="confirm"
          />
        </template>
      </GrammarFooter>
    </template>
  </GrammarModal>
</template>
